import { CONTROLS_TREE, type ControlNode } from './controls-definition';

/**
 * Flatten tree to get all control codes
 */
export function flattenControlCodes(nodes: ControlNode[]): string[] {
  return nodes.flatMap((node) => [
    node.code,
    ...(node.children ? flattenControlCodes(node.children) : []),
  ]);
}

/**
 * Get all descendant codes of a control (for cascade operations)
 */
export function getDescendantCodes(code: string, nodes: ControlNode[] = CONTROLS_TREE): string[] {
  for (const node of nodes) {
    if (node.code === code) {
      return node.children ? flattenControlCodes(node.children) : [];
    }
    if (node.children) {
      const found = getDescendantCodes(code, node.children);
      if (found.length > 0) return found;
    }
  }
  return [];
}

/**
 * Find node by code
 */
export function findNodeByCode(
  code: string,
  nodes: ControlNode[] = CONTROLS_TREE,
): ControlNode | null {
  for (const node of nodes) {
    if (node.code === code) return node;
    if (node.children) {
      const found = findNodeByCode(code, node.children);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Find subtree by code (for rendering specific module)
 */
export function findSubtree(
  code: string,
  nodes: ControlNode[] = CONTROLS_TREE,
): ControlNode | null {
  return findNodeByCode(code, nodes);
}

/**
 * Check if any ancestor toggle of the given code is disabled.
 * This is used to determine if a control should be visually disabled
 * because its parent toggle is off.
 *
 * @param code - The control code to check
 * @param enabledCodes - Set of currently enabled control codes
 * @returns true if an ancestor toggle is disabled
 */
export function isAncestorToggleDisabled(
  code: string,
  enabledCodes: Set<string> | string[],
): boolean {
  const enabledSet = enabledCodes instanceof Set ? enabledCodes : new Set(enabledCodes);
  const parts = code.split(':');

  // Check each ancestor level (from root to immediate parent)
  for (let i = 1; i < parts.length; i++) {
    const ancestorCode = parts.slice(0, i).join(':');
    const ancestorNode = findNodeByCode(ancestorCode, CONTROLS_TREE);

    // If ancestor is a toggle and it's not enabled, this control should be disabled
    if (ancestorNode?.isToggle && !enabledSet.has(ancestorCode)) {
      return true;
    }
  }

  return false;
}

/**
 * Get all ancestor codes for a given control code
 */
export function getAncestorCodes(code: string): string[] {
  const parts = code.split(':');
  const ancestors: string[] = [];

  for (let i = 1; i < parts.length; i++) {
    ancestors.push(parts.slice(0, i).join(':'));
  }

  return ancestors;
}

/**
 * Get all toggle codes (codes where isToggle is true)
 */
export function getAllToggleCodes(nodes: ControlNode[] = CONTROLS_TREE): string[] {
  return nodes.flatMap((node) => [
    ...(node.isToggle ? [node.code] : []),
    ...(node.children ? getAllToggleCodes(node.children) : []),
  ]);
}
